import clsx from "clsx";
import { useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { KTSVG } from "../../../../_metronic/helpers";
import { RootState } from "../../../../setup";
import ImagePreviewAtom from "../../../components/atoms/ImagePreviewAtom";
import ComponentModalTemplate from "../../../components/templates/ComponentModalTemplate";
import { useUserSections } from "../../../context/UserSectionContext";
import { userSections } from "../../../enums/userSections";
import { createSection, setAsDefaultSection } from "../../../services/sectionService";
import { addSignature, deleteImage, userImage } from "../../../services/usersService";
import { capitalizeStr, getURLImage } from "../../../services/utilsService";
import { AuthSignatureModal } from "../../claims/components/atoms/AuthSignatureModal";
import CheckAtomComponent from "../../../components/atoms/CheckAtomComponent";
import { useAutomaticClaim } from "../../../hooks/users/useAutomaticClaim";
import { useSignature } from "../../../hooks/users/profile/useSignature";
import ImageUploader from "../../../components/molecules/ImageUploader";
import { actions } from "../../../modules/auth";
import { getUserByToken } from "../../../modules/auth/redux/AuthCRUD";

const UsersProfile = () => {
    const dispatch = useDispatch();
    const user = useSelector((state: RootState) => state.auth.user, shallowEqual);
    const { sections, loading: loadingSections, loadSections } = useUserSections();
    const { automatic, loading: autoLoading, setValue } = useAutomaticClaim();
    const {  signatureUrl, loading: signLoading, change } = useSignature();
    const [image, setImage] = useState<File | null>(null);
    const [loadingPicture, setLoadingPicture] = useState(false);

    const _createSection = async (section: userSections, isDefault: boolean) => {
        const _response = await Swal.fire({
            icon: 'success',
            title: 'Are you sure?',
            text: `The '${capitalizeStr(section)}' section will be created${isDefault?' and set as the default route.':''}`,
            showCancelButton: true,
            confirmButtonText: 'Yes, create it',
            cancelButtonText: 'Cancel',
            customClass: {
                confirmButton: 'btn-success text-white'
            },
            scrollbarPadding: false,
            heightAuto: false   
        });
        if(!_response.isConfirmed)
            return;
        await createSection(section, isDefault);
        await loadSections();
    }

    const _setAsDefault = async (id: number, section: userSections) => {
        const _response = await Swal.fire({
            icon: 'warning',
            title: 'Are you sure?',
            text: `The '${capitalizeStr(section)}' section will be set as the default route.`,
            showCancelButton: true,
            confirmButtonText: 'Yes, set it',
            cancelButtonText: 'Cancel',
            customClass: {
                confirmButton: 'btn-warning text-white'
            },
            scrollbarPadding: false,
            heightAuto: false   
        });
        if(!_response.isConfirmed)
            return;
        await setAsDefaultSection(id, true);
        await loadSections();
    }

    const _handleSelectSection = (section: userSections) => {
        if(loadingSections)
            return;
        
        const _section = sections.find(s => s.section === section);
        if(!_section)
            _createSection(section, sections.length===0);
        else if (!_section.isDefault)
            _setAsDefault(_section.id, section);
    }
    
    const getButtonClass = (section: userSections) => {
        const _existed = sections.find(s => s.section === userSections[section])
        if(!_existed)
            return 'btn-secondary';
        return _existed.isDefault ? 'btn-primary': 'btn-outline-primary'
    }

    const _handleAddSignature = () => {
        const _fetch = async () => {
            const _authModal = await ComponentModalTemplate<string>({
                children: <AuthSignatureModal confirmLabel="Add"/>,
                options: {
                    title: 'Add signature',
                    showCloseButton: false,
                    showCancelButton: false,
                    showConfirmButton: false,
                    scrollbarPadding: false,
                    heightAuto: false,
                    width: '600px'
                }
            });
            if(!_authModal.isConfirmed || !_authModal.value)
                return;
            change(_authModal.value);
        }
        _fetch();
    }

    const _handleRemoveImage = () => {
        if(!!image)
            return setImage(null);
        const _fetch = async () => {
            try {
                if(!user)
                    return;
                setLoadingPicture(true);
                await deleteImage(user.id);
                const data = await getUserByToken()
                dispatch(actions.setUser(data));
            }
            finally {
                setLoadingPicture(false);
            }
        }
        _fetch();
    }

    const _handleSaveImage = () => {
        const _fetch = async () => {
            try {
                if(!user || !image)
                    return;
                setLoadingPicture(true);
                await userImage(user.id, image);
                const data = await getUserByToken()
                dispatch(actions.setUser(data));
            }
            finally {
                setLoadingPicture(false);
                setImage(null);
            }
        }
        _fetch();
    }

    return (
        <div className="card shadow-sm mb-10">
            <div className="card-body">
                <div className="d-flex flex-wrap flex-row gap-5">
                    <div className="w-auto position-relative p-0 d-flex flex-column" style={{ height: 'fit-content' }}>
                        <ImageUploader
                            preview={user?.profile.getProfilePicture? getURLImage(user?.profile.getProfilePicture): undefined}
                            onSetImage={setImage}
                            onRemoveImage={_handleRemoveImage}
                            loading={loadingPicture}
                            buttonClass="flex-column w-100"
                        />
                        { (!!image && !loadingPicture) && (
                            <button
                                type="button"
                                className="btn btn-sm btn-dark font-weight-bold w-100 mt-2"
                                onClick={_handleSaveImage}
                                disabled={loadingPicture}
                            >
                                { loadingPicture ? (
                                    <div className="text-center">
                                        <span className="spinner-border spinner-border-sm align-middle me-2" />
                                        Loading...
                                    </div>
                                ) :(
                                    <>
                                        <i className="bi bi-floppy2"></i> 
                                        Save
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                    <div>
                        <span className="text-gray-800 text-hover-primary fs-2 fw-bolder me-1">
                            {`${user?.firstName} ${user?.lastName}`}
                            <KTSVG
                                path='/media/icons/duotune/general/gen026.svg'
                                className='svg-icon-1 svg-icon-primary ms-1'
                            />
                        </span>
                        <span className='fw-bold fs-6 d-flex align-items-center text-gray-400 text-hover-primary mb-2 mt-2'>
                            <KTSVG
                                path='/media/icons/duotune/communication/com011.svg'
                                className='svg-icon-4 me-1'
                            />
                            {user?.email}
                        </span>
                        <div className="d-flex mt-auto flex-wrap gap-6 flex-column">
                            <div className="d-flex flex-column border border-gray-300 border-dashed rounded min-w-125px py-3 px-4">
                                <span className="fs-4 fw-bolder text-gray-400 mb-2">Company</span>
                                <span className="fw-bold fs-6 text-dark">{user?.profile.companyName}</span>
                            </div>

                            <div className="d-flex flex-column border border-gray-300 border-dashed rounded min-w-125px py-3 px-4">
                                <span className="fs-4 fw-bolder text-gray-400 mb-2">Role</span>
                                <span className="fw-bold fs-6 text-dark">{user?.roleDescription}</span>
                            </div>

                            <div className="d-flex flex-column border border-gray-300 border-dashed rounded py-3 px-4">
                                <span className="fs-4 fw-bolder text-gray-400 mb-2">Access preference</span>
                                <div className="d-flex flex-row gap-2">
                                    {[userSections.ASSETS, userSections.CLAIM].map(section => (
                                        <button key={section} className={clsx("btn btn-sm flex-fill", getButtonClass(section))}  onClick={() => _handleSelectSection(section)}>
                                            {capitalizeStr(section)}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            
                            <div className="d-flex flex-column border border-gray-300 border-dashed rounded py-3 px-4">
                                <span className="fs-4 fw-bolder text-gray-400 mb-2">Save signature</span>
                                { signLoading ? (
                                    <div className="text-center">
                                        <span className="spinner-border spinner-border-sm align-middle me-2" />
                                        Loading...
                                    </div>
                                ):
                                <>
                                    { !!signatureUrl &&  
                                        <ImagePreviewAtom 
                                            image={signatureUrl}  
                                                styles={{
                                                minWidth: '160px',
                                                width: '250px',
                                                height: '100px',
                                                boxShadow: "none"
                                            }}
                                            className="m-auto mb-2"    
                                        />
                                    }
                                    <button className="btn btn-sm btn-primary" onClick={_handleAddSignature}>
                                        { !!signatureUrl ? 'Change Signature': 'Add Signature'}
                                    </button>
                                </>
                                }
                            </div>

                            <div className="d-flex flex-column border border-gray-300 border-dashed rounded py-3 px-4">
                                <div className="d-flex flex-row align-items-center gap-2">
                                    {autoLoading ? (
                                        <span className="spinner-border spinner-border-sm align-middle me-2" />
                                    ): (
                                        <CheckAtomComponent 
                                            value={automatic}
                                            onClick={(value: boolean) => setValue(!value)}
                                        />
                                    )}
                                    Manage automatic authorization for claim requests
                                </div>
                            </div>


                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default UsersProfile;